import { useState, useCallback } from 'react';

// ─── CopyButton ───────────────────────────────────────────────────────────────
// The core workaround for cross-posting: copy text to clipboard, then
// open the Facebook Marketplace create page (or native app via URL scheme).
//
// Props:
//   textToCopy — string to write to clipboard
//   openUrl    — optional URL to open after copy (Facebook Marketplace)
//   label      — button label text (default "Copy")
//   variant    — "primary" | "secondary" | "ghost"
export default function CopyButton({
  textToCopy,
  openUrl,
  label = 'Copy',
  variant = 'secondary',
}) {
  const [status, setStatus] = useState('idle'); // idle | copied | error

  const handleCopy = useCallback(async () => {
    if (!textToCopy) return;

    try {
      // ── Attempt modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // ── Fallback for older WebViews (including some in-app browsers)
        legacyCopy(textToCopy);
      }

      setStatus('copied');

      // If there's a URL to open (Facebook), do it right after copy
      if (openUrl) {
        // Small delay so the user sees the "Copied!" confirmation before context switches
        setTimeout(() => {
          openFacebook(openUrl);
        }, 300);
      }

      // Reset status after 2.5s
      setTimeout(() => setStatus('idle'), 2500);
    } catch (err) {
      console.warn('[CopyButton] Clipboard write failed:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2500);
    }
  }, [textToCopy, openUrl]);

  const isCopied = status === 'copied';
  const isError = status === 'error';

  if (variant === 'primary') {
    return (
      <>
        <button
          className={`copy-btn copy-btn--primary ${isCopied ? 'copy-btn--success' : ''} ${isError ? 'copy-btn--error' : ''}`}
          onClick={handleCopy}
          aria-label={isCopied ? 'Copied to clipboard' : label}
          aria-live="polite"
        >
          <span className="copy-btn__icon" aria-hidden="true">
            {isCopied ? '✓' : isError ? '⚠' : '⧉'}
          </span>
          <span>
            {isCopied ? 'Copied! Opening Facebook…' : isError ? 'Copy failed — try again' : label}
          </span>
          {!isCopied && !isError && openUrl && (
            <span className="copy-btn__fb-icon" aria-hidden="true">↗</span>
          )}
        </button>

        <CopyStyles />
      </>
    );
  }

  if (variant === 'ghost') {
    return (
      <>
        <button
          className={`copy-btn copy-btn--ghost ${isCopied ? 'copy-btn--ghost-success' : ''}`}
          onClick={handleCopy}
          aria-label={isCopied ? 'Copied' : `Copy ${label}`}
        >
          <span aria-hidden="true">{isCopied ? '✓' : '⧉'}</span>
          <span>{isCopied ? 'Copied' : label}</span>
        </button>

        <CopyStyles />
      </>
    );
  }

  // Default: secondary
  return (
    <>
      <button
        className={`copy-btn copy-btn--secondary ${isCopied ? 'copy-btn--success' : ''}`}
        onClick={handleCopy}
        aria-label={isCopied ? 'Copied to clipboard' : label}
      >
        <span aria-hidden="true">{isCopied ? '✓' : '⧉'}</span>
        <span>{isCopied ? 'Copied!' : label}</span>
      </button>

      <CopyStyles />
    </>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Opens Facebook Marketplace — tries native app deep link first, then web
function openFacebook(webUrl) {
  // Facebook Marketplace native deep link
  const fbDeepLink = 'fb://marketplace/create';

  // Detect if on mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isMobile) {
    // Try to open native app; if it fails after 1.5s, fall back to web
    const appOpened = window.open(fbDeepLink, '_self');

    // Fallback: if app didn't open (user might not have FB installed)
    const fallbackTimer = setTimeout(() => {
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    }, 1500);

    // If the app opens, the page will blur — cancel the fallback
    window.addEventListener(
      'blur',
      () => clearTimeout(fallbackTimer),
      { once: true }
    );

    if (!appOpened) {
      clearTimeout(fallbackTimer);
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    }
  } else {
    // Desktop: just open web
    window.open(webUrl, '_blank', 'noopener,noreferrer');
  }
}

// Legacy clipboard fallback for WebViews that don't support Clipboard API
function legacyCopy(text) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
}

// ─── Styles ───────────────────────────────────────────────────────────────────
function CopyStyles() {
  return (
    <style>{`
      .copy-btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        font-family: inherit; font-weight: 500; cursor: pointer; border: none;
        transition: background 0.15s, transform 0.1s;
        -webkit-tap-highlight-color: transparent;
      }
      .copy-btn:active { transform: scale(0.97); }

      .copy-btn--primary {
        width: 100%; height: 44px; padding: 0 20px;
        background: var(--color-primary); color: #EAF3DE;
        border-radius: var(--radius-md); font-size: 14px;
      }
      .copy-btn--primary:hover { background: var(--color-primary-hover); }
      .copy-btn--primary.copy-btn--success { background: var(--success); color: #fff; }
      .copy-btn--primary.copy-btn--error { background: #DC2626; color: #fff; }

      .copy-btn__icon { font-size: 16px; line-height: 1; flex-shrink: 0; }
      .copy-btn__fb-icon { font-size: 13px; margin-left: 2px; opacity: 0.8; }

      .copy-btn--secondary {
        padding: 8px 14px;
        background: var(--color-bg-page); color: var(--color-text-secondary);
        border: 0.5px solid var(--color-border); border-radius: var(--radius-sm); font-size: 13px;
      }
      .copy-btn--secondary:hover { border-color: var(--color-border-dashed); }
      .copy-btn--secondary.copy-btn--success {
        background: rgba(34, 197, 94, 0.08); color: var(--success);
        border-color: rgba(34, 197, 94, 0.3);
      }

      .copy-btn--ghost {
        padding: 4px 8px; background: transparent;
        color: var(--color-text-hint); border-radius: var(--radius-sm);
        font-size: 12px; gap: 4px;
      }
      .copy-btn--ghost:hover { color: var(--color-text-secondary); }
      .copy-btn--ghost-success { color: var(--success) !important; }
    `}</style>
  );
}
